/* eslint-disable max-lines */
import { Chip, Button } from '@/CommonComponents'
import type { IColumnKey, IColumn } from './index'
import type { GridColDef, GridSortModel, useGridApiRef } from '@mui/x-data-grid'
import { COLORS } from '@/lib/constants/colors'
import { getLocalStorageKey } from '@/lib/utils/localStorage'
import { useEffect, useState, useCallback } from 'react'

export function renderArrayCell<T>({ row, key }: { row: T; key: IColumnKey<T> }) {
  const field = row[key as keyof T] as string[]
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        height: '100%',
        alignItems: 'center',
        gap: 5,
        flexWrap: 'wrap',
      }}
    >
      {field.map((item: string, index: number) => {
        return <Chip key={index} label={item} size="small" />
      })}
    </div>
  )
}

export function renderButtonCell<T>({
  row,
  key,
  button,
}: {
  row: T
  key: IColumnKey<T>
  button: { label: string; action: (row: T) => void }
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      {row[key as keyof T] as string}
      <Button
        onClick={() => {
          button.action(row)
        }}
        label={button.label}
        size="small"
        variant="text"
        sx={{ color: 'primary.light' }}
      />
    </div>
  )
}

export function getRenderCell<T>({ button, key, customRender }: IColumn<T>) {
  return function Cell({ row }: { row: T }) {
    if (customRender) return customRender(row)
    if (Array.isArray(row[key as keyof T])) return renderArrayCell<T>({ row, key })
    if (button) return renderButtonCell({ row, key, button })
  }
}

export function translateFromTableToDataGrid<T>(columnDefinition: IColumn<T>[]): GridColDef[] {
  return columnDefinition.map((column) => {
    const { label, key, width, sortComparator, valueGetter } = column
    return {
      headerName: label,
      field: key as string,
      width: width || 150,
      minWidth: width || 100,
      flex: 1,
      type: 'string',
      renderCell: getRenderCell<T>(column),
      sortComparator,
      valueGetter,
    }
  })
}
// This adds an id to each row if it doesn't have one. This is needed for the DataGrid component
export function curateRows<T>(rows: Array<T>): Array<T> {
  return rows.map((row, index) => {
    return { id: index, ...row }
  })
}
const CHECKBOX_WIDTH = 60
const MIN_CELL_HEIGHT = 2.5
export const getDataGridSx = (selectable: boolean = false) => ({
  borderRadius: 2,
  width: '100%',
  maxHeight: '100%',
  '& .MuiDataGrid-columnHeader': {
    background: COLORS.BACKGROUND.SIDEBAR,
    borderBottom: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '0.8rem',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: COLORS.SECONDARY.LIGHT,
  },
  '& .MuiDataGrid-cell': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    display: 'flex',
    alignItems: 'center',
    minHeight: `${MIN_CELL_HEIGHT}rem`,
  },
  '& .MuiDataGrid-virtualScroller': {
    willChange: 'transform',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    willChange: 'transform',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    willChange: 'transform',
  },
  '& .MuiDataGrid-row': {
    minHeight: 'unset !important',
  },
  '& .MuiDataGrid-row--dynamicHeight': {
    minHeight: 'unset !important',
  },
  ...(selectable && {
    '& .MuiDataGrid-columnHeader[data-field="__check__"]': {
      width: `${CHECKBOX_WIDTH}px !important`,
      minWidth: `${CHECKBOX_WIDTH}px !important`,
      maxWidth: `${CHECKBOX_WIDTH}px !important`,
    },
    '& .MuiDataGrid-cell[data-field="__check__"]': {
      width: `${CHECKBOX_WIDTH}px !important`,
      minWidth: `${CHECKBOX_WIDTH}px !important`,
      maxWidth: `${CHECKBOX_WIDTH}px !important`,
    },
  }),
})
export function getColumnVisibilityModel<T>(hiddenColumns: IColumnKey<T>[]) {
  return hiddenColumns.reduce<Record<IColumnKey<T>, boolean>>(
    (acc, column) => ({
      ...acc,
      [column]: false,
    }),
    {} as Record<IColumnKey<T>, boolean>,
  )
}
export function useTableSort(pathname: string) {
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: '', sort: 'asc' }])
  useEffect(() => {
    try {
      const localStorageKey = getLocalStorageKey(pathname, 'tableSort')
      const savedSortModel = localStorage.getItem(localStorageKey)
      if (savedSortModel) {
        setSortModel(JSON.parse(savedSortModel) as GridSortModel)
      }
    } catch {}
  }, [pathname])

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    try {
      const localStorageKey = getLocalStorageKey(pathname, 'tableSort')
      localStorage.setItem(localStorageKey, JSON.stringify(newSortModel))
    } catch {
      // localStorage not available, continue without saving
    }
    setSortModel(newSortModel)
  }
  return { sortModel, onSortModelChange: handleSortModelChange }
}

export function useColumnResize(apiRef: ReturnType<typeof useGridApiRef>, rows: unknown[]) {
  const resizeColumns = useCallback(() => {
    setTimeout(() => {
      try {
        void apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
        })
      } catch {
        return
      }
    }, 300)
  }, [apiRef])

  useEffect(() => {
    if (rows.length > 0) {
      resizeColumns()
    }
  }, [rows])

  useEffect(() => {
    if (rows.length === 0) {
      return
    }
    let timeoutId: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        resizeColumns()
      }, 500)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
    }
  }, [rows])
  return resizeColumns
}
